import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CheckCircle2, Loader2 } from "lucide-react";

const CTA = { demo: "Request a demo", pilot: "Request a pilot scoping call", brochure: "Send me the brief", sales: "Contact sales" };
const DONE = {
  demo: "Thank you — your demo request is in. We'll be in touch shortly to arrange a live walkthrough.",
  pilot: "Thank you — your pilot enquiry is in. We'll reach out to scope a design-partner pilot with you.",
  brochure: "Thank you — we've received your request.",
  sales: "Thank you — a member of the sales team will be in touch within one working day with pricing tailored to your scope.",
};

export function LeadForm({ kind = "demo", source = "", showMessage = true, messagePlaceholder, appendMessage = "" }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", role: "", message: "", company_website: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const { i18n } = useTranslation();
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const message = [form.message, appendMessage].filter(Boolean).join("\n\n");
      const locale = (i18n.language || "en").slice(0, 2);
      await api.post("/leads", { ...form, message, kind, source, locale });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center" data-testid={`lead-success-${kind}`}>
        <CheckCircle2 className="mx-auto text-emerald-600" size={40} />
        <p className="mt-4 text-base font-medium text-emerald-900">{DONE[kind] || DONE.demo}</p>
      </div>
    );
  }

  const field = "w-full rounded-lg border border-black/10 bg-white px-3.5 py-2.5 text-sm text-ek-ink outline-none focus:border-ek-purple focus:ring-2 focus:ring-ek-purple/20";

  return (
    <form onSubmit={submit} className="space-y-3.5" data-testid={`lead-form-${kind}`}>
      <input type="text" tabIndex={-1} autoComplete="off" value={form.company_website}
        onChange={set("company_website")} className="hidden" aria-hidden="true" />
      <div className="grid gap-3.5 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-medium text-ek-ink/70">Full name *</label>
          <input required value={form.name} onChange={set("name")} className={field}
            placeholder="Jane Smith" data-testid="lead-name" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-ek-ink/70">Work email *</label>
          <input required type="email" value={form.email} onChange={set("email")} className={field}
            placeholder="jane@company.com" data-testid="lead-email" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-ek-ink/70">Company *</label>
          <input required value={form.company} onChange={set("company")} className={field}
            placeholder="Acme plc" data-testid="lead-company" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-ek-ink/70">Role</label>
          <input value={form.role} onChange={set("role")} className={field}
            placeholder="Chief Risk Officer" data-testid="lead-role" />
        </div>
      </div>
      {showMessage && (
        <div>
          <label className="mb-1 block text-xs font-medium text-ek-ink/70">What would you like to cover?</label>
          <textarea rows={3} value={form.message} onChange={set("message")} className={field}
            placeholder={messagePlaceholder || "Tell us about your priorities or constraints…"}
            data-testid="lead-message" />
        </div>
      )}
      {error && <p className="text-sm text-red-600" data-testid="lead-error">{error}</p>}
      {appendMessage && (
        <p className="rounded-lg bg-ek-purple/5 px-3 py-2 text-xs text-ek-purple" data-testid="lead-roi-attached">
          Your ROI estimate will be included with this enquiry.
        </p>
      )}
      <button type="submit" disabled={loading}
        className="ek-btn flex w-full items-center justify-center gap-2 py-3 text-sm font-semibold"
        data-testid={`lead-submit-${kind}`}>
        {loading ? <Loader2 size={16} className="animate-spin" /> : null}
        {CTA[kind] || CTA.demo}
      </button>
      <p className="text-center text-[0.7rem] text-ek-ink/65">
        Commercial-in-Confidence. We&apos;ll only use your details to respond to this enquiry.
      </p>
    </form>
  );
}
